// src/hooks/useIA.ts

import { useState } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

type TipoIA =
  | 'descripcion_empresa'
  | 'descripcion_servicio'
  | 'descripcion_servicio_simple'
  | 'descripcion_producto';

const LIMITE = 10;
const VENTANA_MS = 60 * 60 * 1000; // 1 hora en ms
const STORAGE_KEY = 'ia_timestamps';

const getTimestampsRecientes = (): number[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const todos: number[] = JSON.parse(raw);
    const ahora = Date.now();
    return todos.filter((t) => ahora - t < VENTANA_MS);
  } catch {
    return [];
  }
};

const guardarTimestamp = () => {
  const recientes = getTimestampsRecientes();
  recientes.push(Date.now());
  localStorage.setItem(STORAGE_KEY, JSON.stringify(recientes));
};

export function useIA() {
  const [loading, setLoading] = useState(false);
  const [tonoIdx, setTonoIdx] = useState(0);

  const llamadasRestantes = (): number =>
    Math.max(0, LIMITE - getTimestampsRecientes().length);

  const minutosParaLiberar = (): number => {
    const recientes = getTimestampsRecientes();
    if (recientes.length < LIMITE) return 0;
    const masAntiguo = Math.min(...recientes);
    const msRestantes = VENTANA_MS - (Date.now() - masAntiguo);
    return Math.ceil(msRestantes / 60000);
  };

  const generarDescripcion = async (
    tipo: TipoIA,
    params: {
      id_empresa?: string;
      id_servicio?: string;
      id_producto?: string;
    },
    idx?: number
  ): Promise<string> => {
    const recientes = getTimestampsRecientes();

    if (recientes.length >= LIMITE) {
      toast.error(
        `Has alcanzado el límite de ${LIMITE} generaciones por hora. Inténtalo de nuevo en ${minutosParaLiberar()} min.`,
        { duration: 5000 }
      );
      return '';
    }

    try {
      setLoading(true);
      guardarTimestamp();

      const { data, error } = await supabase.functions.invoke('ia-generar', {
        body: { tipo, ...params, tono_idx: idx ?? tonoIdx },
      });

      if (error) {
        Sentry.captureException(error, { extra: { tipo, ...params } }); // 👈
        throw error;
      }

      return data?.resultado ?? '';
    } finally {
      setLoading(false);
    }
  };

  const generarSiguiente = async (
    tipo: TipoIA,
    params: {
      id_empresa?: string;
      id_servicio?: string;
      id_producto?: string;
    }
  ): Promise<string> => {
    const nextIdx = tonoIdx + 1;
    setTonoIdx(nextIdx);
    return generarDescripcion(tipo, params, nextIdx);
  };

  const resetTono = () => setTonoIdx(0);

  return {
    generarDescripcion,
    generarSiguiente,
    resetTono,
    loading,
    llamadasRestantes,
    minutosParaLiberar,
    limite: LIMITE,
  };
}
